import { ArrowLeftOutlined, InfoCircleFilled } from '@ant-design/icons';
import {
  EnumRichiestaModuloDTOStatoCompilazione,
  useGetFrontRichiesteId,
  useGetFrontRichiesteIdModuli,
  useGetFrontTipiDomandaId,
} from '@framework/api-client';
import { useCurrentUser } from '@framework/auth';
import {
  ActionCanResult,
  selectTranslatedInquiryType,
} from '@framework/common-web';
import {
  CardFooterButtons,
  CompilationContainer,
  DelegationMessageKey,
  DownloadOmissisSubmissionButton,
  DownloadSubmissionModuleButton,
  ISubmissionNavigator,
  SUBMISSION_TITLE_FOCUS_ID,
  SubmissionBurdenLogic,
  SubmissionCancelButton,
  SubmissionLogic,
  SubmissionMenuKey,
} from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { ButtonResponsive, FlexRow, useIdFromUrl } from '@framework/ui';
import { Alert, Space } from 'antd';
import { useMemo } from 'react';
import { RouteComponentProps } from 'react-router';
import { NetworkAlertPage } from '../../../../routes/errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../../../../routes/loading/LoadingPage';
import { SubmissionSendButton } from './SubmissionSendButton';
import { SubmissionSummary } from './SubmissionSummary';

interface Params {
  id: string;
}

export interface ICompilationSendCardProps extends RouteComponentProps<Params> {
  handleNavigation: ISubmissionNavigator;
  canSubmit: ActionCanResult;
}

/**
 * Schermata che gestisce l'invio della richiesta e la visualizzazione del riepilogo.
 */
export function CompilationSendCard(props: ICompilationSendCardProps) {
  const { handleNavigation, canSubmit } = props;
  const [submissionId] = useIdFromUrl(props, 'id');
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const {
    data: submission,
    isLoading,
    error,
  } = useGetFrontRichiesteId(submissionId!, {
    query: { keepPreviousData: true, enabled: Boolean(submissionId) },
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const inquiryRequest = useGetFrontTipiDomandaId(submission?.idTipoDomanda!, {
    query: {
      keepPreviousData: true,
      enabled: Boolean(submission?.idTipoDomanda),
      select: selectTranslatedInquiryType,
    },
  });

  const modulesRequest = useGetFrontRichiesteIdModuli(submissionId!, {
    query: {
      keepPreviousData: true,
      enabled: Boolean(submissionId),
    },
  });

  const configurations = useMemo(
    () => inquiryRequest.data?.postCompilazione?.riepilogo,
    [inquiryRequest.data?.postCompilazione?.riepilogo],
  );

  const isCompleted = Boolean(
    modulesRequest.data?.every(
      m =>
        m.statoValidazione?.completato &&
        m.statoCompilazione ===
          EnumRichiestaModuloDTOStatoCompilazione.COMPLETATO,
    ),
  );

  const hasPendingPayments = SubmissionBurdenLogic.hasPendingPayments(
    submission?.statoOneri,
  );
  const hasAfterSendPayments = SubmissionBurdenLogic.hasAfterSendPayments(
    submission?.statoOneri,
  );

  const cannotSendAsDelegate = SubmissionLogic.cannotSendAsDelegate(
    submission,
    currentUser,
  );

  const showSummary = Boolean(
    configurations?.testo != null ||
      SubmissionBurdenLogic.hasBurdens(submission?.statoOneri) ||
      modulesRequest.data != null,
  );

  // Se c'+ più di un modulo, mostriamo il tasto di download per ogni modulo
  const hasInlineDownload =
    modulesRequest.data && modulesRequest.data.length > 1;

  // Se è disabilitato ma il message richiede il disabled, mostro comunque il pulsante
  const isSubmitDisabled =
    !canSubmit.allowed &&
    canSubmit.message === DelegationMessageKey.SUBMIT_BUTTON_DISABLED;

  // Nascondiamo il pulsante di invio se siamo in preview
  const showSubmitButton =
    !SubmissionLogic.isInquiryPreview(inquiryRequest?.data) &&
    (canSubmit.allowed || isSubmitDisabled);

  if (isLoading || modulesRequest.isLoading || inquiryRequest.isLoading) {
    return <LoadingPage noLayout />;
  }

  if (error ?? modulesRequest.error ?? inquiryRequest.error) {
    return (
      <NetworkAlertPage
        noLayout
        message={t(
          'submission-form.errore-in-caricamento-domanda',
          'Si è verificato un errore durante il caricamento della domanda.',
        )}
        error={error ?? modulesRequest.error ?? inquiryRequest.error}
      />
    );
  }

  if (!submissionId) {
    return (
      <NetworkAlertPage
        noLayout
        message={t(
          'submission-form.errore-in-caricamento-domanda',
          'Si è verificato un errore durante il caricamento della domanda.',
        )}
        error={t(
          'submission-form.id-della-richiesta-non-valido',
          'ID della richiesta non valido',
        )}
      />
    );
  }

  const previousText =
    configurations?.testoPulsanteIndietro ?? t('form.indietro', 'Indietro');

  return (
    <>
      <h1 className="sr-only" id={SUBMISSION_TITLE_FOCUS_ID} tabIndex={-1}>
        {t('menu-items.riepilogo', 'Riepilogo')}
      </h1>
      <CompilationContainer>
        {!isCompleted && canSubmit.allowed && (
          <Alert
            description={t(
              'submission-form.errore-moduli-incompleti-per-poter-procedere',
              "Alcuni moduli risultano incompleti o presentano errori. È necessario compilare tutti i moduli per procedere all'invio della richiesta.",
            )}
            message={t(
              'submission-form.compilazione-richiesta-incompleta',
              'Compilazione richiesta incompleta',
            )}
            showIcon
            type="warning"
            style={{ marginBottom: 24 }}
          />
        )}
        {hasPendingPayments && (
          <Alert
            description={t(
              'submission-form.errore-oneri-non-pagati-per-poter-procedere',
              "Alcuni oneri risultano non pagati. È necessario compilare tutti gli oneri obbligatori per procedere all'invio della richiesta.",
            )}
            message={t(
              'submission-form.stato-pagamenti-incompleto',
              'Stato pagamenti incompleto',
            )}
            showIcon
            type="warning"
            style={{ marginBottom: 24 }}
          />
        )}
        {hasAfterSendPayments && (
          <Alert
            message={t(
              'submission-form.inviare-per-procedere-al-pagamento',
              "Procedere all'invio della Richiesta per effettuare il pagamento degli oneri.",
            )}
            showIcon
            icon={<InfoCircleFilled />}
            type="error"
            style={{ marginBottom: 24 }}
          />
        )}
        {showSummary && (
          <SubmissionSummary
            text={configurations?.testo}
            submission={submission}
            modules={modulesRequest.data}
            hasInlineDownload={hasInlineDownload}
          />
        )}
      </CompilationContainer>
      <CardFooterButtons>
        <FlexRow
          right={
            <Space>
              <SubmissionCancelButton />
              {showSummary && !hasInlineDownload && (
                <DownloadSubmissionModuleButton
                  submissionModuleId={modulesRequest.data?.[0]?.id}
                  submissionId={submissionId}
                  isModuleSigned={
                    modulesRequest.data?.[0]?.idFileFirmato != null
                  }
                  ignoreMobile
                />
              )}
              {showSummary && !hasInlineDownload && (
                <DownloadOmissisSubmissionButton
                  submissionId={submissionId}
                  ignoreMobile
                />
              )}
              {/* Se è disabilitato ma il message richiede il disabled, mostro comunque il pulsante */}
              {showSubmitButton && (
                <SubmissionSendButton
                  submissionId={submissionId}
                  text={t('form.invia-la-domanda', 'Invia la domanda')}
                  isCompleted={isCompleted}
                  hasPendingPayments={hasPendingPayments}
                  cannotSendAsDelegate={cannotSendAsDelegate}
                  disabled={!canSubmit.allowed}
                  tooltip={
                    isSubmitDisabled
                      ? t(
                          'form.disable-submit-tooltip',
                          "L'invio è abilitato solo per il titolare o il procuratore.",
                        )
                      : undefined
                  }
                />
              )}
            </Space>
          }
        >
          <ButtonResponsive
            size="large"
            iconLeftComponent={ArrowLeftOutlined}
            iconLeftAriaLabel={previousText}
            // icon={<ArrowLeftOutlined aria-label={previousText} />}
            text={previousText}
            onClick={() => {
              handleNavigation.navigate(SubmissionMenuKey.Report, 'previous');
            }}
          />
        </FlexRow>
      </CardFooterButtons>
    </>
  );
}
